import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Plus, Trash2 } from 'lucide-react'
import type { LandingBlock, LandingJson } from '@/types'

type Row = Record<string, LandingJson>

interface BlockEditorProps {
  block: LandingBlock
  onChange: (block: LandingBlock) => void
}

type FieldType = 'text' | 'textarea' | 'select' | 'checkbox'
interface FieldDef {
  key: string
  label: string
  type?: FieldType
  options?: { value: string; label: string }[]
  placeholder?: string
}

function Field({
  label,
  value,
  onChange,
  textarea,
  placeholder,
  rows,
  mono,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  textarea?: boolean
  placeholder?: string
  rows?: number
  mono?: boolean
}) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">{label}</Label>
      {textarea ? (
        <Textarea
          value={value}
          rows={rows ?? 3}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={mono ? 'font-mono text-xs' : undefined}
        />
      ) : (
        <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  )
}

function ArrayEditor({
  items,
  fields,
  onChange,
  addLabel,
}: {
  items: Row[]
  fields: FieldDef[]
  onChange: (items: Row[]) => void
  addLabel: string
}) {
  const update = (i: number, key: string, val: LandingJson) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it))
    onChange(next)
  }
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i))
  const add = () => {
    const blank: Row = {}
    fields.forEach((f) => (blank[f.key] = f.type === 'checkbox' ? false : ''))
    onChange([...items, blank])
  }
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-md border bg-muted/30 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">#{i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}>
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>
          <div className="grid gap-2">
            {fields.map((f) => {
              if (f.type === 'checkbox') {
                return (
                  <label key={f.key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!it[f.key]}
                      onChange={(e) => update(i, f.key, e.target.checked)}
                    />
                    {f.label}
                  </label>
                )
              }
              if (f.type === 'select') {
                return (
                  <div key={f.key} className="grid gap-1.5">
                    <Label className="text-xs">{f.label}</Label>
                    <select
                      className="h-9 rounded-md border bg-background px-2 text-sm"
                      value={String(it[f.key] ?? '')}
                      onChange={(e) => update(i, f.key, e.target.value)}
                    >
                      {(f.options ?? []).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )
              }
              return (
                <Field
                  key={f.key}
                  label={f.label}
                  placeholder={f.placeholder}
                  textarea={f.type === 'textarea'}
                  value={String(it[f.key] ?? '')}
                  onChange={(v) => update(i, f.key, v)}
                />
              )
            })}
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add}>
        <Plus className="mr-1 h-3.5 w-3.5" />
        {addLabel}
      </Button>
    </div>
  )
}

export const BlockEditor = ({ block, onChange }: BlockEditorProps) => {
  const data = block.data ?? {}
  const setData = (key: string, value: LandingJson) =>
    onChange({ ...block, data: { ...data, [key]: value } })
  const setArr = (key: string, value: Row[]) =>
    onChange({ ...block, data: { ...data, [key]: value } })
  const str = (key: string): string => String(data[key] ?? '')

  switch (block.type) {
    case 'hero':
      return (
        <div className="grid gap-3">
          <Field
            label="Eyebrow (texto pequeño)"
            value={str('eyebrow')}
            onChange={(v) => setData('eyebrow', v)}
          />
          <Field
            label="Badge (opcional)"
            value={str('badge')}
            onChange={(v) => setData('badge', v)}
          />
          <Field label="Título" value={str('title')} onChange={(v) => setData('title', v)} />
          <Field
            label="Subtítulo"
            textarea
            value={str('subtitle')}
            onChange={(v) => setData('subtitle', v)}
          />
          <Field
            label="Texto del botón"
            value={str('cta_label')}
            onChange={(v) => setData('cta_label', v)}
          />
          <Field
            label="Enlace del botón"
            placeholder="#form o https://…"
            value={str('cta_href')}
            onChange={(v) => setData('cta_href', v)}
          />
          <Field
            label="Imagen (URL, opcional)"
            value={str('image')}
            onChange={(v) => setData('image', v)}
          />
        </div>
      )
    case 'features':
      return (
        <div className="grid gap-3">
          <Field
            label="Título de sección"
            value={str('title')}
            onChange={(v) => setData('title', v)}
          />
          <Label className="text-xs">Tarjetas</Label>
          <ArrayEditor
            items={(data.items as Row[]) ?? []}
            onChange={(v) => setArr('items', v)}
            addLabel="Añadir tarjeta"
            fields={[
              { key: 'icon', label: 'Icono (emoji)' },
              { key: 'title', label: 'Título' },
              { key: 'text', label: 'Texto', type: 'textarea' },
            ]}
          />
        </div>
      )
    case 'social_proof':
      return (
        <div className="grid gap-3">
          <Field
            label="Título de sección"
            value={str('title')}
            onChange={(v) => setData('title', v)}
          />
          <Label className="text-xs">Métricas</Label>
          <ArrayEditor
            items={(data.stats as Row[]) ?? []}
            onChange={(v) => setArr('stats', v)}
            addLabel="Añadir métrica"
            fields={[
              { key: 'value', label: 'Valor (ej. +10.000)' },
              { key: 'label', label: 'Etiqueta' },
            ]}
          />
          <Label className="text-xs">Testimonios</Label>
          <ArrayEditor
            items={(data.testimonials as Row[]) ?? []}
            onChange={(v) => setArr('testimonials', v)}
            addLabel="Añadir testimonio"
            fields={[
              { key: 'quote', label: 'Cita', type: 'textarea' },
              { key: 'author', label: 'Autor' },
              { key: 'role', label: 'Cargo' },
              { key: 'avatar', label: 'Avatar (URL)' },
            ]}
          />
        </div>
      )
    case 'cta':
      return (
        <div className="grid gap-3">
          <Field label="Título" value={str('title')} onChange={(v) => setData('title', v)} />
          <Field
            label="Subtítulo"
            textarea
            value={str('subtitle')}
            onChange={(v) => setData('subtitle', v)}
          />
          <Field
            label="Texto del botón"
            value={str('cta_label')}
            onChange={(v) => setData('cta_label', v)}
          />
          <Field
            label="Enlace del botón"
            value={str('cta_href')}
            onChange={(v) => setData('cta_href', v)}
          />
        </div>
      )
    case 'faq':
      return (
        <div className="grid gap-3">
          <Field
            label="Título de sección"
            value={str('title')}
            onChange={(v) => setData('title', v)}
          />
          <ArrayEditor
            items={(data.items as Row[]) ?? []}
            onChange={(v) => setArr('items', v)}
            addLabel="Añadir pregunta"
            fields={[
              { key: 'q', label: 'Pregunta' },
              { key: 'a', label: 'Respuesta', type: 'textarea' },
            ]}
          />
        </div>
      )
    case 'form':
      return (
        <div className="grid gap-3">
          <Field label="Título" value={str('title')} onChange={(v) => setData('title', v)} />
          <Field
            label="Subtítulo"
            textarea
            value={str('subtitle')}
            onChange={(v) => setData('subtitle', v)}
          />
          <Field
            label="Texto del botón"
            value={str('submit_label')}
            onChange={(v) => setData('submit_label', v)}
          />
          <Field
            label="Mensaje de éxito"
            value={str('success_message')}
            onChange={(v) => setData('success_message', v)}
          />
          <Label className="text-xs">Campos del formulario</Label>
          <ArrayEditor
            items={(data.fields as Row[]) ?? []}
            onChange={(v) => setArr('fields', v)}
            addLabel="Añadir campo"
            fields={[
              { key: 'label', label: 'Etiqueta' },
              { key: 'name', label: 'Nombre (clave)' },
              {
                key: 'type',
                label: 'Tipo',
                type: 'select',
                options: [
                  { value: 'text', label: 'Texto' },
                  { value: 'email', label: 'Email' },
                  { value: 'tel', label: 'Teléfono' },
                  { value: 'textarea', label: 'Área de texto' },
                ],
              },
              { key: 'required', label: 'Obligatorio', type: 'checkbox' },
            ]}
          />
        </div>
      )
    case 'rich_text':
      return (
        <Field
          label="HTML"
          textarea
          mono
          rows={8}
          value={str('html')}
          onChange={(v) => setData('html', v)}
        />
      )
    case 'custom_html':
      return (
        <div className="grid gap-3">
          <Field
            label="HTML"
            textarea
            mono
            rows={10}
            value={block.custom_html ?? ''}
            onChange={(v) => onChange({ ...block, custom_html: v })}
          />
          <Field
            label="CSS (opcional)"
            textarea
            mono
            rows={6}
            value={block.custom_css ?? ''}
            onChange={(v) => onChange({ ...block, custom_css: v })}
          />
        </div>
      )
    default:
      return <p className="text-sm text-muted-foreground">Tipo de bloque no editable.</p>
  }
}
